import moment from "moment";

import { Quote } from "types/quotes";

import { aggregateRede } from "./aggregate";
import { TimeFrame, timeFrameToRange } from "./timeFrame";
import { UseRedeData } from "./useRedeData";

// Dataset fictício para apresentação ao cliente (modo demo, ?empresa=demo).
// Nada disto vem da API nem é gravado — é puramente local e determinístico.

export const DEMO_PROFESSIONALS: { id: number; name: string }[] = [
  { id: 1, name: "Dra. Helena Marques" },
  { id: 2, name: "Dr. Rafael Andrade" },
  { id: 3, name: "Dra. Beatriz Lima" },
  { id: 4, name: "Dr. Gustavo Pereira" },
  { id: 5, name: "Dra. Camila Souza" },
  { id: 6, name: "Dr. Thiago Ramos" },
  { id: 7, name: "Dra. Larissa Costa" },
  { id: 8, name: "Dr. Eduardo Nunes" },
];

// Percentual repassado ao médico por profissional — varia para deixar o
// dropdown de split mais ilustrativo na apresentação.
const DOCTOR_RATE: Record<number, number> = {
  1: 0.9,
  2: 0.88,
  3: 0.85,
  4: 0.92,
  5: 0.87,
  6: 0.86,
  7: 0.89,
  8: 0.84,
};

const PATIENTS = [
  "João Silva",
  "Maria Oliveira",
  "Pedro Santos",
  "Ana Costa",
  "Carlos Souza",
  "Fernanda Lima",
  "Lucas Almeida",
  "Juliana Rocha",
  "Marcos Dias",
  "Patrícia Gomes",
  "Roberto Nunes",
  "Aline Carvalho",
];

// status 6 = Concluído, 5 = Em Agendamento (ambos contam como transacionado).
const SEEDS: {
  daysAgo: number;
  profId: number;
  value: number;
  status: number;
}[] = [
  // Última semana — cobre todos os 8 médicos + volume extra
  { daysAgo: 0, profId: 1, value: 1200, status: 6 },
  { daysAgo: 1, profId: 2, value: 2500, status: 6 },
  { daysAgo: 2, profId: 3, value: 890, status: 5 },
  { daysAgo: 3, profId: 4, value: 1800, status: 6 },
  { daysAgo: 4, profId: 5, value: 450, status: 6 },
  { daysAgo: 5, profId: 6, value: 3200, status: 5 },
  { daysAgo: 6, profId: 7, value: 620, status: 6 },
  { daysAgo: 0, profId: 8, value: 1500, status: 6 },
  { daysAgo: 1, profId: 1, value: 750, status: 6 },
  { daysAgo: 2, profId: 4, value: 2100, status: 6 },
  { daysAgo: 3, profId: 6, value: 980, status: 6 },
  { daysAgo: 5, profId: 2, value: 1350, status: 5 },
  // Resto do mês
  { daysAgo: 12, profId: 3, value: 1700, status: 6 },
  { daysAgo: 15, profId: 5, value: 2400, status: 6 },
  { daysAgo: 18, profId: 1, value: 560, status: 6 },
  { daysAgo: 22, profId: 7, value: 3050, status: 6 },
  { daysAgo: 27, profId: 2, value: 1280, status: 5 },
  // Mais antigos (aparecem em "Último ano")
  { daysAgo: 33, profId: 8, value: 990, status: 6 },
  { daysAgo: 40, profId: 4, value: 2750, status: 6 },
  { daysAgo: 50, profId: 6, value: 1620, status: 6 },
  { daysAgo: 70, profId: 3, value: 880, status: 6 },
  { daysAgo: 95, profId: 5, value: 3400, status: 6 },
  { daysAgo: 120, profId: 7, value: 1150, status: 6 },
  { daysAgo: 160, profId: 1, value: 2950, status: 6 },
];

function buildDemoQuotes(): Quote[] {
  return SEEDS.map((s, i) => {
    const rate = DOCTOR_RATE[s.profId] ?? 0.88;
    const prof =
      DEMO_PROFESSIONALS.find((p) => p.id === s.profId) ??
      { id: s.profId, name: "—" };
    return {
      id: 1000 + i,
      creationDate: moment()
        .subtract(s.daysAgo, "days")
        .format("YYYY-MM-DDTHH:mm:ss"),
      quoteStatus: s.status,
      patient: { id: i + 1, name: PATIENTS[i % PATIENTS.length] },
      professional: { id: prof.id, name: prof.name },
      payment: { value: s.value, compensationValue: Math.round(s.value * rate) },
    } as unknown as Quote;
  });
}

const DEMO_QUOTES = buildDemoQuotes();

// Monta o mesmo formato que o hook real (UseRedeData), honrando os filtros de
// período e de médico sobre o dataset fictício, e reusando aggregateRede para
// que a matemática do split fique idêntica à do painel real.
export function buildDemoData(
  timeFrame: TimeFrame,
  doctorId: number | null
): UseRedeData {
  const { startDate, endDate } = timeFrameToRange(timeFrame);

  const filtered = DEMO_QUOTES.filter((q) => {
    const day = (q.creationDate || "").slice(0, 10);
    const inRange = day >= startDate && day <= endDate;
    const matchDoctor = doctorId == null || q.professional?.id === doctorId;
    return inRange && matchDoctor;
  });

  const { perProfessional, transactions } = aggregateRede(
    filtered,
    DEMO_PROFESSIONALS
  );

  return {
    professionals: DEMO_PROFESSIONALS,
    perProfessional,
    transactions,
    isLoading: false,
    error: false,
    isTruncated: false,
  };
}
